/**
 * Phase 4A.5A — the browser side of POST /api/assistant/messages.
 *
 * One function, one request shape, one closed result vocabulary. It exists so
 * the panel component never touches axios, never sees an HTTP status and never
 * holds a value the backend did not explicitly allow through its three-field
 * projection.
 *
 * **Transport is not re-implemented here.** The shared `lib/api` axios instance
 * owns the `Authorization` header (read fresh from the Supabase SDK on every
 * request) and the `X-Organization-ID` header. Adding a second client would
 * mean a second place where those two could drift or be forgotten. The only
 * per-call config this module passes is an `AbortSignal` — cancellation is a
 * transport concern that cannot be expressed any other way, and it carries no
 * authority: it can start no request and can alter no header.
 *
 * **The request vocabulary is exactly the backend's** (`_AssistantMessageIn`,
 * `extra="forbid"`): a message and an optional two-field page hint. There is no
 * organization, user, role, session, conversation, release, snapshot, model,
 * provider, tool or limit field — not filtered out on the way, simply never
 * assembled, so a future edit has nothing to widen. A server that started
 * trusting such a field would be the bug; a client that sends one is how it
 * gets found late.
 *
 * **The response vocabulary is a closed allowlist.** `kind` must be one of two
 * values and `code` one of two; anything else — a new kind, a renamed code, an
 * HTML error page, a proxy's JSON, a truncated body — resolves to the single
 * generic unavailable state. Failing closed here is what keeps an unexpected
 * body from reaching the conversation as if it were an answer.
 *
 * Every field is read as an OWN property. Destructuring would walk the
 * prototype chain, so a polluted `Object.prototype` — from anywhere in the
 * bundle — could otherwise make an empty body render as an answer.
 */
import api from "./api";

/** The backend's `MAX_USER_INPUT_CHARS`. Refused, never truncated. */
export const MAX_ASSISTANT_MESSAGE_CHARS = 1500;

/**
 * Every state the panel can render. The first three mirror the backend's
 * `kind`/`code` pair; the rest are transport outcomes the backend never names.
 */
export const ASSISTANT_RESULT = Object.freeze({
  ANSWER: "answer",
  INSUFFICIENT_EVIDENCE: "insufficient_evidence",
  UNAVAILABLE: "assistant_unavailable",
  UNAUTHENTICATED: "unauthenticated",
  FORBIDDEN: "forbidden",
  RATE_LIMITED: "rate_limited",
  INVALID_REQUEST: "invalid_request",
  FAILED: "failed",
  // Never rendered: the only caller aborts a turn as part of ending the
  // session that owned it, and a state for a conversation that no longer
  // exists has no reader. It is named so the drop is explicit, not incidental.
  ABORTED: "aborted",
});

// `quowada.assistant.runtime_service.ALL_KINDS`.
const KIND_ANSWER = "answer";
const KIND_DENIAL = "denial";

// `quowada.assistant.response_copy.ALL_RESPONSE_CODES`, mapped to our states.
const DENIAL_CODES = Object.freeze({
  insufficient_evidence: ASSISTANT_RESULT.INSUFFICIENT_EVIDENCE,
  assistant_unavailable: ASSISTANT_RESULT.UNAVAILABLE,
});

// Transport refusals the panel has distinct Bulgarian copy for. Every other
// status — 4xx, 5xx, a proxy error, a network failure — is one generic failure.
const STATUS_RESULTS = Object.freeze({
  401: ASSISTANT_RESULT.UNAUTHENTICATED,
  403: ASSISTANT_RESULT.FORBIDDEN,
  422: ASSISTANT_RESULT.INVALID_REQUEST,
  429: ASSISTANT_RESULT.RATE_LIMITED,
});

/** Own-key lookup that a crafted `__proto__`/`constructor` value cannot satisfy. */
const owns = (table, key) => Object.prototype.hasOwnProperty.call(table, key);

/** Read one OWN field, so nothing inherited can stand in for a real one. */
const own = (source, key) => (owns(source, key) ? source[key] : undefined);

/** The generic unavailable outcome. The one thing an unknown body becomes. */
function unavailable() {
  return { result: ASSISTANT_RESULT.UNAVAILABLE, text: null };
}

/**
 * Build the request body from an allowlist, never by spreading an object.
 *
 * `page_hint` is included only when `pageHintFromPath` produced one and its
 * shape is still exactly the two permitted keys — the backend forbids extras,
 * and a 422 for a field we added by accident would look like a user error.
 */
export function buildAssistantRequest(message, pageHint) {
  const body = { message };
  if (
    pageHint &&
    typeof pageHint === "object" &&
    typeof pageHint.page_type === "string"
  ) {
    const objectId =
      typeof pageHint.object_id === "string" && pageHint.object_id
        ? pageHint.object_id
        : null;
    body.page_hint = { page_type: pageHint.page_type, object_id: objectId };
  }
  return body;
}

/**
 * Normalize one 200 body into a state. Fails closed on anything unexpected.
 *
 * An `answer` is the only path on which server text is ever rendered, and it
 * requires the full triple to be well formed: the sealed `kind`, a non-empty
 * string message and no code. A denial's Bulgarian sentence comes from the
 * frontend's own copy keyed by the allowlisted `code`, so no server string is
 * shown for a failure the user cannot act on.
 */
export function normalizeAssistantResponse(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return unavailable();
  }
  const kind = own(data, "kind");
  const message = own(data, "message");
  const code = own(data, "code");
  if (kind === KIND_ANSWER) {
    if (typeof message !== "string" || message === "") return unavailable();
    if (code !== null && code !== undefined) return unavailable();
    return { result: ASSISTANT_RESULT.ANSWER, text: message };
  }
  if (kind === KIND_DENIAL) {
    const mapped =
      typeof code === "string" && owns(DENIAL_CODES, code)
        ? DENIAL_CODES[code]
        : null;
    return mapped ? { result: mapped, text: null } : unavailable();
  }
  return unavailable();
}

/** True for the rejection axios raises when its `AbortSignal` fires. */
function isAbort(err, signal) {
  return (
    (signal && signal.aborted) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

/** Map a rejected request to a state without surfacing any server detail. */
function normalizeAssistantError(err, signal) {
  if (isAbort(err, signal)) {
    return { result: ASSISTANT_RESULT.ABORTED, text: null };
  }
  const status = err?.response?.status;
  const mapped =
    typeof status === "number" && owns(STATUS_RESULTS, status)
      ? STATUS_RESULTS[status]
      : null;
  // Deliberately no `detail`, no status code, no axios message: an internal
  // slug is not something a user can act on, and it is exactly what must not
  // reach the screen.
  return { result: mapped || ASSISTANT_RESULT.FAILED, text: null };
}

/**
 * Send one stateless turn. Never rejects — the panel renders a state, not a
 * catch block, and a thrown error is how an internal detail escapes.
 *
 * `signal` is the caller's only lever on an in-flight turn. It is forwarded as
 * the sole per-call option so the shared client keeps owning everything else.
 */
export async function sendAssistantMessage({ message, pageHint, signal }) {
  try {
    const response = await api.post(
      "/assistant/messages",
      buildAssistantRequest(message, pageHint),
      { signal },
    );
    return normalizeAssistantResponse(response?.data);
  } catch (err) {
    return normalizeAssistantError(err, signal);
  }
}

export default sendAssistantMessage;
